import React, { useCallback, useState, useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { FileError } from 'react-dropzone'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import TabButton from 'LuxKit/Tabs/TabButton'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useStateWithSetters from 'hooks/useStateWithSetters'
import DocumentDropzone from 'tripPlanner/components/Documents/DocumentDropzone'
import FileUploadError from 'tripPlanner/components/Documents/FileUploadError'
import FileUploadPreview from 'tripPlanner/components/Documents/FileUploadPreview'
import FileUploadSuccess from 'tripPlanner/components/Documents/FileUploadSuccess'
import MyDocuments from 'tripPlanner/components/Documents/MyDocuments'
import {
  useTripDocuments,
  useUploadDocument,
} from 'tripPlanner/hooks/api/documents'
import * as TripDocumentsMapper from 'tripPlanner/mappers/documents'
import type { FullTrip } from 'tripPlanner/types/common'
import type { TripDocumentWithItemInfo } from 'tripPlanner/types/documents'
import {
  flattenDocuments,
  getAttachedItemDisplayName,
} from 'tripPlanner/utils/documents'

interface Props {
  trip: FullTrip
  tripItemId?: string
}

function DocumentsModal({ trip, tripItemId }: Props) {
  const intl = useIntl()
  const tripItem = trip.items.find((item) => item.id === tripItemId)

  const [currentView, setView] = useStateWithSetters([
    'selectUpload',
    'myDocuments',
    'uploading',
  ])

  const modalElement = useModalElementContext()
  const onClose = () => modalElement.resolve()

  const [dropzoneError, setDropzoneError] = useState<FileError | null>(null)

  const {
    mutate: uploadFile,
    isLoading: isUploading,
    isSuccess,
    isError: isUploadError,
    progress: uploadProgress,
    variables: uploadingFile,
    data: document,
    error: uploadError,
    retryUpload,
  } = useUploadDocument({
    tripId: trip.id,
    tripItemId,
    onMutate: () => setDropzoneError(null),
  })

  const isError = isUploadError || !!dropzoneError

  const onSelectFile = useCallback(
    (file: File) => {
      setDropzoneError(null)
      uploadFile(file)
      setView.uploading()
    },
    [setView, uploadFile],
  )

  const { data: documents } = useTripDocuments({ tripId: trip.id })

  const filteredMyDocuments = useMemo(() => {
    if (!documents) {
      return []
    }

    return flattenDocuments(trip, documents).filter(
      (document: TripDocumentWithItemInfo) =>
        !document.item || document.item.id === tripItemId,
    )
  }, [documents, trip, tripItemId])

  const onRejectFile = useCallback(
    (fileError: FileError) => {
      setDropzoneError(fileError)
      setView.uploading()
    },
    [setView],
  )

  const fallbackFilename = intl.formatMessage({
    id: '_CVNaMlwRcnuy-ypNZKY',
    defaultMessage: 'File',
    description: 'Fallback filename when the uploading file name is unavailable',
  })

  let heading = tripItem ?
      intl.formatMessage(
        {
          id: 'LPoPvSKkBq7kt8XVAD6M',
          defaultMessage: 'Attach document to {displayName}',
          description: 'Heading for document attachment modal showing the trip item name',
        },
        { displayName: getAttachedItemDisplayName(tripItem) },
      ) :
      intl.formatMessage({
        id: 'ghFBHk1qIQJIao59idrD',
        defaultMessage: 'Upload document',
        description: 'Section heading for document upload modal in the trip planner documents section',
      })
  if (isUploading) {
    heading = intl.formatMessage({
      id: 'vR4TaJdZttGYWfsEHAhE',
      defaultMessage: 'Uploading document',
      description: 'Heading shown while document upload is in progress',
    })
  } else if (isSuccess) {
    heading = intl.formatMessage({
      id: 'QbNTnWRHuTNz5zyxH29e',
      defaultMessage: 'Upload successful',
      description: 'Heading shown after document upload completes successfully',
    })
  } else if (isError) {
    heading = intl.formatMessage({
      id: '8pD4bYebN61Js_VKO6np',
      defaultMessage: 'Upload failed',
      description: 'Heading shown when document upload fails in the trip planner documents section',
    })
  }

  return (
    <ModalBase>
      <ModalHeader
        title={heading}
        onCloseButtonClick={onClose}
        onBackButtonClick={
          currentView === 'uploading' && isError ?
            setView.selectUpload :
            undefined
        }
      />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={16}>
            {!!filteredMyDocuments.length &&
              (currentView === 'selectUpload' ||
                currentView === 'myDocuments') && (
              <TabButtonsGroup>
                <TabButton
                  size="large"
                  isActive={currentView === 'selectUpload'}
                  onClick={setView.selectUpload}
                >
                  {intl.formatMessage({
                    id: '24S7uXc-On7NiCr4S0OW',
                    defaultMessage: 'Add new',
                    description: 'Tab label to upload a new document in the trip planner documents section',
                  })}
                </TabButton>
                <TabButton
                  size="large"
                  isActive={currentView === 'myDocuments'}
                  onClick={setView.myDocuments}
                >
                  {intl.formatMessage({
                    id: 'Uq31z-e3EXt_TotF0qRi',
                    defaultMessage: 'My documents',
                    description: 'Tab label to view existing uploaded documents in the trip planner documents section',
                  })}
                </TabButton>
              </TabButtonsGroup>
            )}

            {/* Add new */}
            {currentView === 'selectUpload' && (
              <DocumentDropzone
                onSelectFile={onSelectFile}
                onRejectFile={onRejectFile}
                disabled={isUploading}
              />
            )}

            {currentView === 'uploading' && (
              <>
                {isUploading && (
                  <FileUploadPreview
                    filename={uploadingFile ? uploadingFile.name : fallbackFilename}
                    progress={uploadProgress}
                  />
                )}
                {isSuccess && (
                  <FileUploadSuccess
                    document={TripDocumentsMapper.mapTripDocument(document)}
                  />
                )}
                {isError && (
                  <FileUploadError
                    uploadError={uploadError}
                    dropzoneError={dropzoneError}
                    onRequestRetry={retryUpload}
                  />
                )}
              </>
            )}

            {/* My documents */}
            {currentView === 'myDocuments' && (
              <MyDocuments
                tripId={trip.id}
                tripItemId={tripItemId}
                documents={filteredMyDocuments}
              />
            )}
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
      {currentView === 'uploading' && (
        <>
          {isSuccess && (
            <ModalFooter
              primaryActionProps={{
                children: intl.formatMessage({
                  id: '2CrDasRab3Uenc0dFVsF',
                  defaultMessage: 'Done',
                  description: 'Button to close modal after successful document upload',
                }),
                onClick: onClose,
              }}
            />
          )}
          {isError && (
            <ModalFooter
              primaryActionProps={{
                children: intl.formatMessage({
                  id: '7RYsv3TjB0WzFspzvv7P',
                  defaultMessage: 'Close',
                  description: 'Button to close modal after failed document upload',
                }),
                onClick: onClose,
              }}
            />
          )}
        </>
      )}
      {currentView === 'myDocuments' && (
        <ModalFooter
          primaryActionProps={{
            children: intl.formatMessage({
              id: '7ovMUX94j8IzIW-nPjPQ',
              defaultMessage: 'Done',
              description: 'Action button to close the my documents view in the trip planner documents section',
            }),
            onClick: onClose,
          }}
        />
      )}
    </ModalBase>
  )
}

export default DocumentsModal
